// Lightweight SWR-style in-memory cache for widget API calls.
// Lives at module level so it persists across component mounts/unmounts
// (i.e., when the user closes and reopens the feedback panel).
//
// Pattern: serve stale data IMMEDIATELY (no loading spinner on reopens),
// then refetch in background and update the UI when fresh data arrives.
// TTL is short (30s) so data stays reasonably fresh without hammering the API.

const CACHE_TTL_MS = 30_000;

type Entry<T> = { data: T; ts: number };
const store = new Map<string, Entry<unknown>>();

export function cacheGet<T>(key: string): T | null {
  const entry = store.get(key) as Entry<T> | undefined;
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL_MS) {
    store.delete(key);
    return null;
  }
  return entry.data;
}

export function cacheSet<T>(key: string, data: T): void {
  store.set(key, { data, ts: Date.now() });
}

export function cacheInvalidate(keyPrefix: string): void {
  for (const key of store.keys()) {
    if (key.startsWith(keyPrefix)) store.delete(key);
  }
}
